export const CLAIM_ADDITIONAL_FIELD_TYPES = [
  { value: 'TEXT', label: '텍스트' },
  { value: 'TEXTAREA', label: '긴 문장' },
  { value: 'NUMBER', label: '숫자' },
  { value: 'DATE', label: '날짜' },
  { value: 'SELECT', label: '선택목록' },
  { value: 'RADIO', label: '단일선택' },
  { value: 'CHECKBOX', label: '체크박스' },
]

export function claimAdditionalFieldTypeLabel(value) {
  const found = CLAIM_ADDITIONAL_FIELD_TYPES.find((item) => item.value === value)
  return found ? found.label : '텍스트'
}

export const CLAIM_ADDITIONAL_FIELD_COMMON_MODE = 'COMMON'
export const CLAIM_ADDITIONAL_FIELD_PER_INSURER_MODE = 'PER_INSURER'

const ALWAYS_PER_INSURER_FIELD_KEYS = new Set([
  'virtualFaxNumber',
])

const RESIDENT_ID_DEPENDENT_FIELD_KEYS = new Set([
  'residentIdIssueDate',
  'residentIdIssuer',
  'beneficiaryResidentIdIssueDate',
  'beneficiaryResidentIdIssuer',
])

const DRIVER_LICENSE_DEPENDENT_FIELD_KEYS = new Set([
  'driverLicenseNumber',
  'driverLicenseNumberRegion',
  'driverLicenseNumberSecond',
  'driverLicenseNumberThird',
  'driverLicenseNumberFourth',
  'driverLicenseSerialNumber',
  'driverLicenseIssueDate',
  'driverLicenseIssuer',
  'beneficiaryDriverLicenseNumber',
  'beneficiaryDriverLicenseNumberRegion',
  'beneficiaryDriverLicenseNumberSecond',
  'beneficiaryDriverLicenseNumberThird',
  'beneficiaryDriverLicenseNumberFourth',
  'beneficiaryDriverLicenseSerialNumber',
  'beneficiaryDriverLicenseIssueDate',
  'beneficiaryDriverLicenseIssuer',
])

export function isChoiceAdditionalFieldType(value) {
  return value === 'SELECT' || value === 'RADIO'
}

function originalAdditionalFieldKey(field) {
  return String(field?.originalFieldKey || field?.valueKey || field?.fieldKey || '').trim()
}

export function normalizeAdditionalFieldDefinitions(fields) {
  if (!Array.isArray(fields)) return []
  return fields
    .filter((field) => field && field.fieldKey && field.label)
    .map((field, idx) => ({
      ...field,
      inputType: CLAIM_ADDITIONAL_FIELD_TYPES.some((item) => item.value === field.inputType)
        ? field.inputType
        : 'TEXT',
      required: !!field.required,
      sensitive: !!field.sensitive,
      active: field.active !== false,
      sortOrder: Number.isFinite(Number(field.sortOrder)) ? Number(field.sortOrder) : idx,
      options: Array.isArray(field.options) ? field.options : [],
    }))
    .sort((a, b) => {
      const diff = Number(a.sortOrder || 0) - Number(b.sortOrder || 0)
      return diff || String(a.fieldKey).localeCompare(String(b.fieldKey))
    })
}

function optionsSignature(options) {
  if (!Array.isArray(options) || options.length === 0) return ''
  return options
    .map((option) => `${String(option?.value ?? '').trim()}\u001f${String(option?.label ?? '').trim()}`)
    .join('\u001e')
}

function additionalFieldShapeSignature(field) {
  return [
    field.inputType || 'TEXT',
    field.required ? 'required' : '',
    field.sensitive ? 'sensitive' : '',
    optionsSignature(field.options),
  ].join('\u001f')
}

function additionalFieldScopeLabel(insurerNames, selectedCount) {
  const names = Array.isArray(insurerNames) ? insurerNames.filter(Boolean) : []
  if (selectedCount > 1 && names.length === selectedCount) return '선택 보험사 공통'
  if (names.length === 1) return names[0]
  if (names.length > 1) return `${names[0]} 외 ${names.length - 1}곳`
  return ''
}

export function additionalFieldGroupModeKey(group) {
  return String(group?.valueKey || group?.fieldKey || group?.originalFieldKey || '').trim()
}

export function additionalFieldInsurerValueKey(group, insurerId) {
  const baseKey = additionalFieldGroupModeKey(group)
  const id = String(insurerId ?? '').trim()
  return baseKey && id ? `${baseKey}__insurer_${id}` : baseKey
}

export function isAdditionalFieldAlwaysPerInsurer(group) {
  return ALWAYS_PER_INSURER_FIELD_KEYS.has(originalAdditionalFieldKey(group))
}

export function additionalFieldInputModeForGroup(group, modeByGroupKey = {}) {
  const key = additionalFieldGroupModeKey(group)
  const requested = key ? modeByGroupKey?.[key] : null
  const canSplit = Array.isArray(group?.insurerIds) && group.insurerIds.length > 1
  if (canSplit && isAdditionalFieldAlwaysPerInsurer(group)) {
    return CLAIM_ADDITIONAL_FIELD_PER_INSURER_MODE
  }
  return canSplit && requested === CLAIM_ADDITIONAL_FIELD_PER_INSURER_MODE
    ? CLAIM_ADDITIONAL_FIELD_PER_INSURER_MODE
    : CLAIM_ADDITIONAL_FIELD_COMMON_MODE
}

function additionalFieldIdentityCondition(field) {
  const key = originalAdditionalFieldKey(field)
  if (RESIDENT_ID_DEPENDENT_FIELD_KEYS.has(key)) {
    return {
      controllerKey: key.startsWith('beneficiary') ? 'beneficiaryIdentityType' : 'identityType',
      expected: 'RESIDENT_ID',
    }
  }
  if (DRIVER_LICENSE_DEPENDENT_FIELD_KEYS.has(key)) {
    return {
      controllerKey: key.startsWith('beneficiary') ? 'beneficiaryIdentityType' : 'identityType',
      expected: 'DRIVER_LICENSE',
    }
  }
  return null
}

export function effectiveAdditionalFieldInputModeForGroup(group, allFields = [], modeByGroupKey = {}) {
  const baseMode = additionalFieldInputModeForGroup(group, modeByGroupKey)
  if (baseMode === CLAIM_ADDITIONAL_FIELD_PER_INSURER_MODE) {
    return baseMode
  }
  const condition = additionalFieldIdentityCondition(group)
  if (!condition) {
    return baseMode
  }
  const fields = Array.isArray(allFields) ? allFields : []
  const controller = fields.find((candidate) => originalAdditionalFieldKey(candidate) === condition.controllerKey)
  const canSplit = Array.isArray(group?.insurerIds) && group.insurerIds.length > 1
  if (canSplit && controller && additionalFieldInputModeForGroup(controller, modeByGroupKey) === CLAIM_ADDITIONAL_FIELD_PER_INSURER_MODE) {
    return CLAIM_ADDITIONAL_FIELD_PER_INSURER_MODE
  }
  return baseMode
}

function identityValueMatches(value, expected) {
  const v = String(value ?? '').trim().toUpperCase()
  if (!v) return false
  if (expected === 'RESIDENT_ID') {
    return v.includes('주민') || v === 'ID_CARD' || v === 'RESIDENT_ID' || v === 'RESIDENT_CARD'
  }
  if (expected === 'DRIVER_LICENSE') {
    return v.includes('운전') || v.includes('DRIVER')
  }
  return true
}

function visibleControllerValue(controller, values, insurerId, modeByGroupKey) {
  const valueMap = values && typeof values === 'object' ? values : {}
  const targetId = String(insurerId ?? '')
  const mode = additionalFieldInputModeForGroup(controller, modeByGroupKey)
  const valueKey = mode === CLAIM_ADDITIONAL_FIELD_PER_INSURER_MODE && targetId
    ? additionalFieldInsurerValueKey(controller, targetId)
    : (controller.valueKey || controller.fieldKey)
  return valueMap[valueKey]
}

export function isAdditionalFieldVisible(field, allFields = [], values = {}, insurerId = '', modeByGroupKey = {}) {
  const condition = additionalFieldIdentityCondition(field)
  if (!condition) return true
  const fields = Array.isArray(allFields) ? allFields : []
  const controller = fields.find((candidate) => originalAdditionalFieldKey(candidate) === condition.controllerKey)
  if (!controller) return true
  return identityValueMatches(visibleControllerValue(controller, values, insurerId, modeByGroupKey), condition.expected)
}

export function buildAdditionalFieldGroupsForInsurers(definitionsByInsurerId, insurers = []) {
  const source = definitionsByInsurerId && typeof definitionsByInsurerId === 'object'
    ? definitionsByInsurerId
    : {}
  const insurerNameById = new Map(
    (Array.isArray(insurers) ? insurers : [])
      .filter((insurer) => insurer?.id != null)
      .map((insurer) => [String(insurer.id), String(insurer.name || '').trim()]),
  )
  const selectedCount = insurerNameById.size
  const byShape = new Map()

  Object.entries(source).forEach(([insurerIdRaw, fields]) => {
    const insurerId = String(insurerIdRaw)
    const insurerName = insurerNameById.get(insurerId) || ''
    normalizeAdditionalFieldDefinitions(fields)
      .filter((field) => field.active !== false)
      .forEach((field) => {
        const originalFieldKey = String(field.fieldKey || '').trim()
        if (!originalFieldKey) return
        const shapeKey = `${originalFieldKey}\u001d${additionalFieldShapeSignature(field)}`
        if (!byShape.has(shapeKey)) {
          byShape.set(shapeKey, {
            ...field,
            originalFieldKey,
            valueKey: originalFieldKey,
            insurerIds: [],
            insurerNames: [],
            minSortOrder: Number.isFinite(Number(field.sortOrder)) ? Number(field.sortOrder) : 0,
          })
        }
        const group = byShape.get(shapeKey)
        if (!group.insurerIds.includes(insurerId)) {
          group.insurerIds.push(insurerId)
          if (insurerName) group.insurerNames.push(insurerName)
        }
        const sortOrder = Number.isFinite(Number(field.sortOrder)) ? Number(field.sortOrder) : 0
        group.minSortOrder = Math.min(group.minSortOrder, sortOrder)
      })
  })

  const groups = Array.from(byShape.values()).sort((a, b) => {
    const diff = Number(a.minSortOrder || 0) - Number(b.minSortOrder || 0)
    if (diff) return diff
    return String(a.label || a.originalFieldKey).localeCompare(String(b.label || b.originalFieldKey), 'ko')
  })

  const fieldKeyGroupCounts = new Map()
  groups.forEach((group) => {
    const key = group.originalFieldKey
    fieldKeyGroupCounts.set(key, (fieldKeyGroupCounts.get(key) || 0) + 1)
  })

  const fieldKeySeenCounts = new Map()
  return groups.map((group) => {
    const originalFieldKey = group.originalFieldKey
    const seen = (fieldKeySeenCounts.get(originalFieldKey) || 0) + 1
    fieldKeySeenCounts.set(originalFieldKey, seen)
    const disambiguate = (fieldKeyGroupCounts.get(originalFieldKey) || 0) > 1
    const valueKey = disambiguate
      ? `__claim_additional_${originalFieldKey}_${seen}`
      : originalFieldKey
    const insurerNames = [...new Set(group.insurerNames)]
    return {
      ...group,
      fieldKey: valueKey,
      valueKey,
      originalFieldKey,
      insurerNames,
      scopeLabel: additionalFieldScopeLabel(insurerNames, selectedCount),
    }
  })
}

export function buildAdditionalExtraFieldsForInsurer(groups, values, insurerId, modeByGroupKey = {}) {
  const targetId = String(insurerId ?? '')
  if (!targetId) return {}
  const valueMap = values && typeof values === 'object' ? values : {}
  const out = {}

  ;(Array.isArray(groups) ? groups : []).forEach((group) => {
    if (!Array.isArray(group.insurerIds) || !group.insurerIds.map(String).includes(targetId)) return
    if (!isAdditionalFieldVisible(group, groups, valueMap, targetId, modeByGroupKey)) return
    const key = String(group.originalFieldKey || '').trim()
    if (!key) return
    const mode = effectiveAdditionalFieldInputModeForGroup(group, groups, modeByGroupKey)
    const valueKey = mode === CLAIM_ADDITIONAL_FIELD_PER_INSURER_MODE
      ? additionalFieldInsurerValueKey(group, targetId)
      : (group.valueKey || group.fieldKey)
    const raw = valueMap[valueKey]
    if (group.inputType === 'CHECKBOX') {
      if (raw === 'Y' || raw === 'N') out[key] = raw
      return
    }
    const value = raw == null ? '' : String(raw).trim()
    if (value) out[key] = value
  })

  return out
}

export function optionsToText(options) {
  if (!Array.isArray(options) || options.length === 0) return ''
  return options
    .map((option) => {
      const value = String(option?.value ?? '').trim()
      const label = String(option?.label ?? '').trim()
      if (!value && !label) return ''
      if (!label || value === label) return value || label
      return `${value}|${label}`
    })
    .filter(Boolean)
    .join('\n')
}

export function textToOptions(text) {
  return String(text || '')
    .split(/\r?\n|,/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const pipeIndex = line.indexOf('|')
      if (pipeIndex >= 0) {
        const value = line.slice(0, pipeIndex).trim()
        const label = line.slice(pipeIndex + 1).trim()
        return { value: value || label, label: label || value }
      }
      return { value: line, label: line }
    })
    .filter((option) => option.value)
}

export function blankClaimAdditionalField(sortOrder = 0) {
  return {
    fieldKey: '',
    label: '',
    inputType: 'TEXT',
    required: false,
    sensitive: false,
    options: [],
    optionsText: '',
    sortOrder,
    active: true,
  }
}

export function prepareAdditionalFieldForEdit(field, idx = 0) {
  const type = CLAIM_ADDITIONAL_FIELD_TYPES.some((item) => item.value === field?.inputType)
    ? field.inputType
    : 'TEXT'
  const options = Array.isArray(field?.options) ? field.options : []
  return {
    fieldKey: String(field?.fieldKey || ''),
    label: String(field?.label || ''),
    inputType: type,
    required: !!field?.required,
    sensitive: !!field?.sensitive,
    options,
    optionsText: optionsToText(options),
    sortOrder: Number.isFinite(Number(field?.sortOrder)) ? Number(field.sortOrder) : idx,
    active: field?.active !== false,
  }
}

export function prepareAdditionalFieldForSave(field, idx = 0) {
  const inputType = CLAIM_ADDITIONAL_FIELD_TYPES.some((item) => item.value === field?.inputType)
    ? field.inputType
    : 'TEXT'
  return {
    fieldKey: String(field?.fieldKey || '').trim(),
    label: String(field?.label || '').trim(),
    inputType,
    required: !!field?.required,
    sensitive: !!field?.sensitive,
    options: isChoiceAdditionalFieldType(inputType) ? textToOptions(field?.optionsText) : [],
    sortOrder: Number.isFinite(Number(field?.sortOrder)) ? Number(field.sortOrder) : idx,
    active: field?.active !== false,
  }
}

export function validateRequiredAdditionalFields(fields, values, modeByGroupKey = {}) {
  const defs = normalizeAdditionalFieldDefinitions(fields).filter((field) => field.active !== false)
  const valueMap = values && typeof values === 'object' ? values : {}
  for (const field of defs) {
    if (!field.required) continue
    const mode = effectiveAdditionalFieldInputModeForGroup(field, defs, modeByGroupKey)
    if (mode === CLAIM_ADDITIONAL_FIELD_PER_INSURER_MODE) {
      const ids = Array.isArray(field.insurerIds) ? field.insurerIds.map(String).filter(Boolean) : []
      const names = Array.isArray(field.insurerNames) ? field.insurerNames : []
      for (let i = 0; i < ids.length; i += 1) {
        if (!isAdditionalFieldVisible(field, defs, valueMap, ids[i], modeByGroupKey)) continue
        const raw = valueMap[additionalFieldInsurerValueKey(field, ids[i])]
        if (raw == null || String(raw).trim() === '') {
          const scope = names[i] ? `(${names[i]})` : ''
          return `${field.label}${scope}을(를) 입력해 주세요.`
        }
      }
      continue
    }
    if (!isAdditionalFieldVisible(field, defs, valueMap, '', modeByGroupKey)) continue
    const raw = valueMap[field.valueKey || field.fieldKey]
    if (raw == null || String(raw).trim() === '') {
      return `${field.label}을(를) 입력해 주세요.`
    }
  }
  return null
}
